const knownBotUserAgentPattern = /bot|crawl|spider|slurp|facebookexternalhit|externalagent|archive\.org/i;
const networkErrorCodes = [
  'ERR_NETWORK',
  'ECONNABORTED',
  'ECONNRESET',
  'ENETUNREACH',
  'ETIMEDOUT',
  'EAI_AGAIN',
];

/**
 * Search-engine/social-preview crawlers (bingbot, meta-externalagent, etc.)
 * hit the visualizer with no real session, so auth-gated bootstrap calls fail
 * for them by design. That failure is not user-facing pain, and left
 * unfiltered it dominates the fatal-error inbox (crawler traffic accounted
 * for the large majority of "Request failed with status code 403" reports).
 */
export function isBotUserAgent(userAgent) {
  const ua = userAgent || (typeof navigator !== 'undefined' && navigator.userAgent) || '';

  return knownBotUserAgentPattern.test(ua);
}

function shouldIgnoreResourceError(source) {
  return typeof source === 'string'
    && source.indexOf('/content/Views/dashboard/images/icons/pj-scope-icons/') !== -1
    && /\.svg(\?|$)/i.test(source);
}

/**
 * Ignores opaque cross-origin script failures where the browser strips the real
 * filename and stack, so the error cannot be attributed to visualizer code.
 */
function isCrossOriginScriptError(event) {
  return !event.error
    && !event.filename
    && (event.message === 'Script error.' || event.message === 'Script error');
}

/**
 * Ignores a known legacy Custom HTML tag in GTM-K2RNL44 that calls global
 * jQuery on host pages where window.jQuery is not guaranteed.
 */
function isIgnoredGtmJqueryError(event) {
  const message = event.message || '';
  const source = event.filename || '';
  const stack = event.error && event.error.stack || '';
  const gtmContainerId = 'GTM-K2RNL44';

  return message.indexOf('jQuery is not defined') !== -1
    && (
      source.indexOf(gtmContainerId) !== -1
      || stack.indexOf(gtmContainerId) !== -1
      || source.indexOf('googletagmanager.com/gtm.js') !== -1
      || stack.indexOf('googletagmanager.com/gtm.js') !== -1
    );
}

/**
 * ResizeObserver reports these browser-generated notifications through the
 * window error event even though observation continues on the next frame.
 */
export function isResizeObserverLoopError(event) {
  const message = event.message || event.error && event.error.message || '';

  return message === 'ResizeObserver loop completed with undelivered notifications.'
    || message === 'ResizeObserver loop limit exceeded';
}

/**
 * A script loaded from an extension bundle (any browser, any vendor) rather
 * than from our own origin.
 */
function isExtensionProtocolSource(source) {
  return typeof source === 'string'
    && /^(chrome|moz|safari-web|ms-browser|edge)-extension:\/\//i.test(source);
}

/**
 * Extensions that inject a script directly into the page's own JS context
 * (as wallet providers like MetaMask do) have that script report itself as a
 * bare filename with no origin/path, because the browser has no real URL to
 * attribute it to. Nothing in our own bundle ever shows up that way - our
 * code always loads from a full same-origin URL. This is vendor-agnostic:
 * it does not depend on any extension's script name or product name.
 */
function isBareInjectedScriptName(source) {
  return typeof source === 'string'
    && /^[\w.-]+\.m?js$/i.test(source);
}

function isExtensionSource(source) {
  return isExtensionProtocolSource(source) || isBareInjectedScriptName(source);
}

/**
 * Pulls the "source:line:col" location out of one stack trace line, in
 * either V8 ("at name (source:line:col)" / "at source:line:col") or
 * Firefox/Safari ("name@source:line:col") format, and strips the
 * trailing ":line:col" to leave just the source.
 */
function extractStackFrameSource(line) {
  const trimmed = line.trim();
  const parenMatch = trimmed.match(/\(([^)]+)\)\s*$/);
  const location = parenMatch
    ? parenMatch[1]
    : trimmed.replace(/^at\s+/, '').replace(/^[^@\s]*@/, '');

  return location.replace(/:\d+:\d+$/, '');
}

/**
 * Scans an error stack's call sites for frames that originate from a browser
 * extension, using the same vendor-agnostic signals as isExtensionSource.
 */
function stackHasExtensionSource(stack) {
  if (typeof stack !== 'string' || !stack) {
    return false;
  }

  return stack.split('\n').some((line) => isExtensionSource(extractStackFrameSource(line)));
}

/**
 * True when an error originates from browser extension code (a wallet
 * provider, ad blocker, translator, etc.) rather than from the visualizer
 * itself, regardless of which extension it is.
 */
export function isExtensionInjectedError(source, stack) {
  return isExtensionSource(source) || stackHasExtensionSource(stack);
}

/**
 * Window-level browser errors that should not trigger the fatal recovery popup
 * or email logging because they are known browser/external-script noise.
 */
function shouldIgnoreWindowError(event) {
  return isCrossOriginScriptError(event)
    || isIgnoredGtmJqueryError(event)
    || isResizeObserverLoopError(event)
    || isExtensionInjectedError(event.filename, event.error && event.error.stack);
}

/**
 * Ignores intentionally canceled async work, usually aborted HTTP requests.
 */
export function isCanceledError(error) {
  if (!error) {
    return false;
  }

  const message = error.message || '';

  return error.name === 'CanceledError'
    || error.code === 'ERR_CANCELED'
    || message === 'canceled';
}

export function getHttpErrorStatus(error) {
  if (!error) {
    return null;
  }

  const responseStatus = error.response && error.response.status;
  const directStatus = error.status;
  const numericStatus = Number(responseStatus || directStatus);

  if (Number.isFinite(numericStatus) && numericStatus >= 100 && numericStatus <= 599) {
    return numericStatus;
  }

  const message = typeof error === 'string' ? error : error.message || '';
  const statusMatch = String(message).match(/status code\s+(\d{3})\b/i);

  return statusMatch ? Number(statusMatch[1]) : null;
}

export function isHttpAccessError(error) {
  const status = getHttpErrorStatus(error);

  return status === 401 || status === 403;
}

/**
 * Browser/network failures do not describe an application bug. Treat them as a
 * connectivity state so they do not trigger fatal recovery logging.
 */
export function isNetworkConnectivityError(error) {
  if (!error) {
    return false;
  }

  if (getHttpErrorStatus(error)) {
    return false;
  }

  const message = typeof error === 'string' ? error : error.message || '';
  const code = error.code || '';
  const responseUrl = error.request && error.request.responseURL || '';
  const source = error.source || '';
  const isBrowserOffline = typeof navigator !== 'undefined' && navigator.onLine === false;

  return isBrowserOffline
    || networkErrorCodes.indexOf(code) !== -1
    || message === 'Network Error'
    || message.indexOf('Network Error') !== -1
    || message.indexOf('Failed to fetch') !== -1
    || message.indexOf('Network request failed') !== -1
    || message.indexOf('Load failed') !== -1
    || /timeout of \d+ms exceeded/i.test(message)
    || message.indexOf('ERR_INTERNET_DISCONNECTED') !== -1
    || responseUrl.indexOf('ERR_INTERNET_DISCONNECTED') !== -1
    || source.indexOf('ERR_INTERNET_DISCONNECTED') !== -1;
}

function reportWindowError(event, reportFatalError, captureResourceErrors) {
  const target = event.target || event.srcElement;

  if (target && target !== window && (target.src || target.href)) {
    if (!captureResourceErrors) {
      return;
    }

    const source = target.src || target.href;

    if (shouldIgnoreResourceError(source)) {
      return;
    }

    reportFatalError('resource-error', {
      name: 'ResourceError',
      message: 'Resource failed to load',
      source,
    });
    return;
  }

  if (shouldIgnoreWindowError(event)) {
    if (isResizeObserverLoopError(event) && typeof event.preventDefault === 'function') {
      event.preventDefault();
    }
    return;
  }

  reportFatalError('window-error', {
    message: event.message,
    name: (event.error && event.error.name) || 'Error',
    stack: event.error && event.error.stack,
    source: event.filename,
    line: event.lineno,
    column: event.colno,
  });
}

/**
 * Keeps expected browser/network promise failures out of fatal error reporting.
 * Canceled requests are intentional, and connectivity failures are surfaced
 * through the offline UI instead of being logged as product defects.
 */
function reportUnhandledRejection(event, reportFatalError) {
  if (isCanceledError(event.reason)) {
    return;
  }

  if (isNetworkConnectivityError(event.reason)) {
    event.preventDefault();
    reportFatalError('network-connectivity-error', event.reason);
    return;
  }

  if (isHttpAccessError(event.reason)) {
    event.preventDefault();
    reportFatalError('request-access-error', event.reason);
    return;
  }

  if (getHttpErrorStatus(event.reason)) {
    event.preventDefault();
    reportFatalError('http-request-error', event.reason);
    return;
  }

  const reason = event.reason || {};

  if (isExtensionInjectedError(null, reason.stack)) {
    return;
  }

  reportFatalError('unhandled-rejection', event.reason || {
    message: 'Unhandled promise rejection',
  });
}

export function instrumentBrowserRuntime({reportFatalError, captureResourceErrors}) {
  window.addEventListener('error', (event) => reportWindowError(event, reportFatalError, captureResourceErrors), true);
  window.addEventListener('unhandledrejection', (event) => reportUnhandledRejection(event, reportFatalError));
}
